import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../api/axiosConfig';
import { apiErrorText } from '../utils/apiErrors';
import { isoWeek, isoWeekYear } from '../utils/dates';
import { useAuth } from '../context/AuthContext';
import { hasNavPermission } from '../components/ProtectedRoute';
import './SleeverControlListPage.css';
import '../styles/rowActions.css';
import '../styles/topbarButtons.css';
import '../styles/pageHeader.css';
import ExportPreviewModal from '../components/ExportPreviewModal';
import RowProperties from '../components/RowProperties';

const DAYS = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miercoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sabado' },
];

function hexToRgba(hex, alpha) {
  if (!hex) return null;
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Blends a ribbon hex color with white, matching the light tint used for
// row backgrounds on screen, so exported rows read the same way.
function lightenHex(hex, whiteAmount = 0.82) {
  if (!hex) return null;
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  const mix = (c) => Math.round(c * (1 - whiteAmount) + 255 * whiteAmount);
  const toHex = (c) => c.toString(16).padStart(2, '0');
  return `${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

async function downloadWorkbook(workbook, filename) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function weekFileLabel(week) {
  return `Enfunde_${week.farm_name}_S${week.week_number}_${week.year}`.replace(/\s+/g, '_');
}

// Lotes are named "#<number>" — sort by the number, not alphabetically.
function loteNumber(name) {
  return parseInt(String(name).replace(/\D/g, ''), 10) || 0;
}

// Builds the same lote-grouped sheet shown on the Control de Enfunde page:
// one row per bunch sleever, with the Lote and Total-por-lote cells spanning
// every row in that lote's group. The ribbon color is repeated on every row
// (it's constant for the whole week) so it survives if a row is read on its own.
function buildSheetLayout(week, lotes, entriesByLote) {
  const ribbonLabel = week.ribbon_color || '—';
  const sortedLotes = [...lotes]
    .filter((l) => (entriesByLote[l.id] || []).length > 0)
    .sort((a, b) => loteNumber(a.name) - loteNumber(b.name));

  const aoaRows = [];
  const pdfBody = [];
  const merges = []; // { row: startRowIndexWithinAoaRows, span }

  sortedLotes.forEach((lote) => {
    const entries = [...entriesByLote[lote.id]].sort((a, b) => a.id - b.id);
    const loteTotal = entries.reduce((s, e) => s + (e.total || 0), 0);
    const startRow = aoaRows.length;

    entries.forEach((e, idx) => {
      const dayVals = DAYS.map((d) => e[d.key] || 0);
      if (idx === 0) {
        aoaRows.push([lote.name, e.bunch_sleever_name, ...dayVals, e.total, e.melizos || 0, loteTotal, ribbonLabel]);
        pdfBody.push([
          entries.length > 1 ? { content: lote.name, rowSpan: entries.length } : lote.name,
          e.bunch_sleever_name, ...dayVals, e.total, e.melizos || 0,
          entries.length > 1 ? { content: loteTotal, rowSpan: entries.length } : loteTotal,
          ribbonLabel,
        ]);
      } else {
        aoaRows.push(['', e.bunch_sleever_name, ...dayVals, e.total, e.melizos || 0, '', ribbonLabel]);
        pdfBody.push([e.bunch_sleever_name, ...dayVals, e.total, e.melizos || 0, ribbonLabel]);
      }
    });

    if (entries.length > 1) merges.push({ row: startRow, span: entries.length });
  });

  const allEntries = sortedLotes.flatMap((l) => entriesByLote[l.id] || []);
  const daySum = (day) => allEntries.reduce((s, e) => s + (e[day] || 0), 0);
  const totalSum = allEntries.reduce((s, e) => s + (e.total || 0), 0);
  const melizosSum = allEntries.reduce((s, e) => s + (e.melizos || 0), 0);
  const primeraVuelta = daySum('monday') + daySum('tuesday') + daySum('wednesday');
  const segundaVuelta = daySum('thursday') + daySum('friday') + daySum('saturday');
  const totalsRow = ['Total', '', ...DAYS.map((d) => daySum(d.key)), totalSum, melizosSum, totalSum, ribbonLabel];

  return { aoaRows, pdfBody, merges, totalsRow, totalSum, melizosSum, primeraVuelta, segundaVuelta };
}

// The week's Observaciones (comment + number lines) that have anything filled in.
function weekNotes(week) {
  return [1, 2, 3]
    .map((n) => ({ text: week[`note_${n}`] || '', value: week[`note_${n}_value`] }))
    .filter((note) => note.text || note.value != null);
}

// Total/Ha = total ÷ the sheet's hectáreas en producción (a snapshot saved on the sheet by the API).
const formatPerHa = (value) => (value == null
  ? '—' : Number(value).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const formatInt = (value) => Number(value).toLocaleString('es-EC');

// How a sheet's Total/Ha is made up: "1.045 ÷ 20,00 ha" (null when it has no hectares).
function perHaFormula(w) {
  if (w.hectares_in_production == null || Number(w.hectares_in_production) <= 0) return null;
  return `${formatInt(w.total_bags)} ÷ ${formatPerHa(w.hectares_in_production)} ha`;
}

// Hover note for a sheet's Total/Ha, e.g. "1.045 ÷ 20,00 ha = 52,25".
function perHaBreakdown(w) {
  const formula = perHaFormula(w);
  return formula
    ? `${formula} = ${formatPerHa(w.total_per_hectare)}`
    : 'Sin hectáreas en producción registradas para esta semana';
}

// Summary card: the average of the filtered sheets' Total/Ha values (sheets without one are skipped).
function summaryPerHectare(weeks) {
  const values = weeks.filter((w) => w.total_per_hectare != null).map((w) => Number(w.total_per_hectare));
  if (values.length === 0) return null;
  // Add in cents so the total matches the column exactly (no floating-point drift).
  const total = values.reduce((sum, v) => sum + Math.round(v * 100), 0) / 100;
  return total / values.length;
}

// A small note shown while hovering (or focusing) its content. Positioned against the window,
// so the table's scroll box doesn't clip it.
function HoverNote({ note, children }) {
  const [pos, setPos] = useState(null);
  const show = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ left: r.left + r.width / 2, top: r.top });
  };
  return (
    <span
      className="scl-hover-note-anchor"
      tabIndex={0}
      onMouseEnter={show}
      onFocus={show}
      onMouseLeave={() => setPos(null)}
      onBlur={() => setPos(null)}
      aria-label={note}
    >
      {children}
      {pos && (
        <span className="scl-hover-note" role="tooltip" style={{ left: pos.left, top: pos.top }}>{note}</span>
      )}
    </span>
  );
}

// Cinta filter value for sheets without a ribbon.
const NO_RIBBON = '__sin_cinta__';

// The page opens on the current ISO week (the numbering sheets use).
const TODAY = new Date();
const CURRENT_YEAR = String(isoWeekYear(TODAY));
const CURRENT_WEEK = String(isoWeek(TODAY));

const ALL_HEAD = ['Finca', 'Año', 'Semana', 'Cinta', 'Trabajadores', 'Primera Vuelta', 'Segunda Vuelta', 'Total', 'Total/Ha'];
const allRow = (w) => [
  w.farm_name, w.year, w.week_number, w.ribbon_color || '—', w.entry_count,
  w.primera_vuelta, w.segunda_vuelta, w.total_bags, formatPerHa(w.total_per_hectare),
];
const weekHead = () => ['Lote', 'Nombre', ...DAYS.map((d) => d.label), 'Total', 'Melizos', 'Total por Lote', 'Cinta'];

const previewCell = (value, i, tint) => {
  const isSpan = value && typeof value === 'object';
  return (
    <td key={i} rowSpan={isSpan ? value.rowSpan : undefined} style={tint ? { background: tint } : undefined}>
      {isSpan ? value.content : value}
    </td>
  );
};

// Mirrors exportWeekToPDF / exportWeekToExcel: same header lines, table, totals and notes.
function WeekExportPreview({ week, detail }) {
  const tint = week.ribbon_hex_color ? `#${lightenHex(week.ribbon_hex_color)}` : undefined;
  const notes = weekNotes(week);
  return (
    <div>
      <p className="export-preview-heading">HACIENDA {week.farm_name.toUpperCase()}</p>
      <p className="export-preview-line">Año {week.year} · Semana {week.week_number} · Control de Enfunde</p>
      <p className="export-preview-line">Color de Cinta: {week.ribbon_color || '— sin cinta —'}</p>
      <table className="export-preview-table" style={{ marginTop: 8 }}>
        <thead><tr>{weekHead().map((h) => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>
          {detail.pdfBody.map((cells, r) => <tr key={r}>{cells.map((c, i) => previewCell(c, i, tint))}</tr>)}
          <tr className="export-preview-totals">{detail.totalsRow.map((c, i) => previewCell(c, i, tint))}</tr>
        </tbody>
      </table>
      <p className="export-preview-line" style={{ marginTop: 10 }}>
        Primera Vuelta: {detail.primeraVuelta}    Segunda Vuelta: {detail.segundaVuelta}    Total: {detail.totalSum}
        {'    '}Total/Ha: {formatPerHa(week.total_per_hectare)}
        {perHaFormula(week) && ` (${perHaFormula(week)})`}
      </p>
      {notes.length > 0 && (
        <div className="export-preview-notes">
          {notes.map((note, i) => (
            <div key={i} className="export-preview-note">
              <span>{note.text}</span><span>{note.value ?? ''}</span>
            </div>
          ))}
          <div className="export-preview-note export-preview-note-total">
            <span>Total</span><span>{week.notes_total}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Mirrors exportAllToPDF / exportAllToExcel: one row per filtered week, tinted by its ribbon.
function AllWeeksExportPreview({ weeks }) {
  return (
    <div>
      <p className="export-preview-heading">Historial de Control de Enfunde</p>
      <table className="export-preview-table" style={{ marginTop: 8 }}>
        <thead><tr>{ALL_HEAD.map((h) => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>
          {weeks.map((w) => {
            const tint = w.ribbon_hex_color ? `#${lightenHex(w.ribbon_hex_color)}` : undefined;
            return (
              <tr key={w.id}>
                {allRow(w).map((c, i) => previewCell(c, i, tint))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function SleeverControlListPage() {
  const { user } = useAuth();
  const canReopenWeeks = hasNavPermission(user, 'can_reopen_weeks');
  const canDelete = hasNavPermission(user, 'delete_records');
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [farmFilter, setFarmFilter] = useState('');
  const [yearFilter, setYearFilter] = useState(CURRENT_YEAR);
  const [weekFilter, setWeekFilter] = useState(CURRENT_WEEK);
  const [ribbonFilter, setRibbonFilter] = useState(''); // ribbon color, or NO_RIBBON
  const [exportingId, setExportingId] = useState(null);
  // { kind: 'week' | 'all', week?, detail?, loading?, error? } | null
  const [preview, setPreview] = useState(null);
  const [lockActionId, setLockActionId] = useState(null);

  const fetchAll = () => {
    setLoading(true);
    api.get('sleever-weeks/')
      .then((r) => setWeeks(r.data))
      .catch(() => setStatus({ type: 'error', text: 'No se pudieron cargar las hojas guardadas.' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const farmOptions = useMemo(
    () => [...new Set(weeks.map((w) => w.farm_name))].sort(),
    [weeks]
  );
  const yearOptions = useMemo(
    () => [...new Set([CURRENT_YEAR, ...weeks.map((w) => String(w.year)), yearFilter].filter(Boolean))]
      .sort((a, b) => b - a),
    [weeks, yearFilter]
  );

  // Weeks with sheets in the chosen year, plus the current week and the selected one.
  const weekOptions = useMemo(() => {
    const nums = weeks.filter((w) => !yearFilter || String(w.year) === yearFilter).map((w) => String(w.week_number));
    if (!yearFilter || yearFilter === CURRENT_YEAR) nums.push(CURRENT_WEEK);
    if (weekFilter) nums.push(weekFilter);
    return [...new Set(nums)].sort((a, b) => b - a);
  }, [weeks, yearFilter, weekFilter]);
  const ribbonOptions = useMemo(
    () => [...new Set(weeks.map((w) => w.ribbon_color).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [weeks]
  );
  const hasSheetsWithoutRibbon = useMemo(() => weeks.some((w) => !w.ribbon_color), [weeks]);

  const filtered = useMemo(
    () => weeks.filter((w) =>
      (!farmFilter || w.farm_name === farmFilter) &&
      (!yearFilter || String(w.year) === yearFilter) &&
      (!weekFilter || String(w.week_number) === weekFilter) &&
      (!ribbonFilter || (ribbonFilter === NO_RIBBON ? !w.ribbon_color : w.ribbon_color === ribbonFilter))
    ),
    [weeks, farmFilter, yearFilter, weekFilter, ribbonFilter]
  );
  const anyFilter = farmFilter || yearFilter || weekFilter || ribbonFilter;

  const summary = useMemo(() => ({
    sheets: filtered.length,
    workers: filtered.reduce((s, w) => s + w.entry_count, 0),
    bags: filtered.reduce((s, w) => s + w.total_bags, 0),
    perHa: summaryPerHectare(filtered),
  }), [filtered]);

  const exportAllToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Historial');
    const headRow = sheet.addRow(ALL_HEAD);
    headRow.eachCell((cell) => { cell.font = { bold: true }; });

    filtered.forEach((w) => {
      const row = sheet.addRow([
        ...allRow(w).slice(0, -1),
        w.total_per_hectare == null ? '—' : Number(w.total_per_hectare),
      ]);
      row.getCell(ALL_HEAD.length).numFmt = '#,##0.00';
      if (w.ribbon_hex_color) {
        const argb = `FF${lightenHex(w.ribbon_hex_color).toUpperCase()}`;
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
        });
      }
    });
    sheet.columns.forEach((col) => { col.width = 16; });
    await downloadWorkbook(workbook, 'Historial_Control_de_Enfunde.xlsx');
  };

  const exportAllToPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(14);
    doc.text('Historial de Control de Enfunde', 14, 16);
    autoTable(doc, {
      startY: 22,
      head: [ALL_HEAD],
      body: filtered.map(allRow),
      didParseCell: (data) => {
        if (data.section === 'body') {
          const week = filtered[data.row.index];
          if (week?.ribbon_hex_color) {
            data.cell.styles.fillColor = `#${lightenHex(week.ribbon_hex_color)}`;
          }
        }
      },
    });
    doc.save('Historial_Control_de_Enfunde.pdf');
  };

  const loadWeekDetail = async (week) => {
    const [entriesRes, lotesRes] = await Promise.all([
      api.get(`sleever-entries/?week=${week.id}`),
      api.get(`lotes/?farm=${week.farm}`),
    ]);
    const entriesByLote = {};
    entriesRes.data.forEach((e) => {
      if (!entriesByLote[e.lote]) entriesByLote[e.lote] = [];
      entriesByLote[e.lote].push(e);
    });
    return buildSheetLayout(week, lotesRes.data, entriesByLote);
  };

  const openWeekPreview = async (week) => {
    setPreview({ kind: 'week', week, loading: true });
    try {
      const detail = await loadWeekDetail(week);
      setPreview((pv) => (pv && pv.week?.id === week.id ? { ...pv, detail, loading: false } : pv));
    } catch (err) {
      setPreview((pv) => (pv && pv.week?.id === week.id
        ? { ...pv, loading: false, error: 'No se pudo cargar la hoja.' } : pv));
    }
  };

  const handlePreviewDownload = (fmt) => {
    if (preview.kind === 'all') return fmt === 'excel' ? exportAllToExcel() : exportAllToPDF();
    return fmt === 'excel' ? exportWeekToExcel(preview.week) : exportWeekToPDF(preview.week);
  };

  const exportWeekToExcel = async (week) => {
    setExportingId(week.id);
    setStatus(null);
    try {
      const { aoaRows, merges, totalsRow, primeraVuelta, segundaVuelta, totalSum } = await loadWeekDetail(week);
      const headRow = ['Lote', 'Nombre', ...DAYS.map((d) => d.label), 'Total', 'Melizos', 'Total por Lote', 'Cinta'];
      const loteTotalCol = headRow.length - 1; // 1-based column for 'Total por Lote' ('Cinta' is last, not spanned)

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet(`Semana ${week.week_number}`);
      sheet.addRow([`HACIENDA ${week.farm_name.toUpperCase()}`]);
      sheet.addRow([`Año ${week.year} · Semana ${week.week_number} · Control de Enfunde`]);
      sheet.addRow([`Color de Cinta: ${week.ribbon_color || '— sin cinta —'}`]);
      sheet.addRow([]);
      const headExcelRow = sheet.addRow(headRow);
      headExcelRow.eachCell((cell) => { cell.font = { bold: true }; });

      const dataStartRowNumber = sheet.rowCount + 1;
      aoaRows.forEach((r) => sheet.addRow(r));
      const totalsExcelRow = sheet.addRow(totalsRow);
      totalsExcelRow.eachCell((cell) => { cell.font = { bold: true }; });

      sheet.addRow([]);
      sheet.addRow([`Primera Vuelta: ${primeraVuelta}`]);
      sheet.addRow([`Segunda Vuelta: ${segundaVuelta}`]);
      sheet.addRow([`Total: ${totalSum}`]);
      sheet.addRow([`Total/Ha: ${formatPerHa(week.total_per_hectare)}`
        + (perHaFormula(week) ? ` (${perHaFormula(week)})` : '')]);

      const notes = weekNotes(week);
      if (notes.length > 0) {
        sheet.addRow([]);
        const noteRow = (text, value, bold) => {
          const row = sheet.addRow([text, '', '', '', '', '', value ?? '']);
          sheet.mergeCells(row.number, 1, row.number, 6);
          row.getCell(7).alignment = { horizontal: 'right' };
          if (bold) row.eachCell((cell) => { cell.font = { bold: true }; });
          return row;
        };
        notes.forEach((note) => noteRow(note.text, note.value, false));
        const totalRow = noteRow('Total', week.notes_total, true);
        totalRow.getCell(7).border = { top: { style: 'thin' } };
      }

      if (week.ribbon_hex_color) {
        const argb = `FF${lightenHex(week.ribbon_hex_color).toUpperCase()}`;
        for (let rn = dataStartRowNumber; rn <= dataStartRowNumber + aoaRows.length; rn++) {
          sheet.getRow(rn).eachCell({ includeEmpty: true }, (cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
          });
        }
      }

      merges.forEach((m) => {
        const startRow = dataStartRowNumber + m.row;
        const endRow = startRow + m.span - 1;
        sheet.mergeCells(startRow, 1, endRow, 1);
        sheet.mergeCells(startRow, loteTotalCol, endRow, loteTotalCol);
      });

      sheet.columns.forEach((col) => { col.width = 14; });
      await downloadWorkbook(workbook, `${weekFileLabel(week)}.xlsx`);
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo exportar esa hoja.' });
    } finally {
      setExportingId(null);
    }
  };

  const exportWeekToPDF = async (week) => {
    setExportingId(week.id);
    setStatus(null);
    try {
      const { pdfBody, totalsRow, primeraVuelta, segundaVuelta, totalSum } = await loadWeekDetail(week);
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text(`HACIENDA ${week.farm_name.toUpperCase()}`, 14, 14);
      doc.setFontSize(10);
      doc.text(`Año ${week.year} · Semana ${week.week_number} · Control de Enfunde`, 14, 20);
      doc.text(`Color de Cinta: ${week.ribbon_color || '— sin cinta —'}`, 14, 26);
      autoTable(doc, {
        startY: 31,
        head: [['Lote', 'Nombre', ...DAYS.map((d) => d.label), 'Total', 'Melizos', 'Total por Lote', 'Cinta']],
        body: [...pdfBody, totalsRow],
        styles: { fontSize: 9 },
        didParseCell: (data) => {
          if (data.section === 'body' && week.ribbon_hex_color) {
            data.cell.styles.fillColor = `#${lightenHex(week.ribbon_hex_color)}`;
          }
        },
      });
      const finalY = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(10);
      doc.text(
        `Primera Vuelta: ${primeraVuelta}    Segunda Vuelta: ${segundaVuelta}    Total: ${totalSum}`
          + `    Total/Ha: ${formatPerHa(week.total_per_hectare)}`
          + (perHaFormula(week) ? ` (${perHaFormula(week)})` : ''),
        14, finalY,
      );

      const notes = weekNotes(week);
      if (notes.length > 0) {
        const valueX = 130;
        let y = finalY + 10;
        doc.setFontSize(9);
        notes.forEach((note) => {
          doc.text(doc.splitTextToSize(note.text, valueX - 20)[0] || '', 14, y);
          doc.text(note.value != null ? String(note.value) : '', valueX, y, { align: 'right' });
          y += 5;
        });
        doc.line(valueX - 20, y - 2, valueX, y - 2);
        doc.setFont(undefined, 'bold');
        doc.text('Total', 14, y + 3);
        doc.text(String(week.notes_total), valueX, y + 3, { align: 'right' });
        doc.setFont(undefined, 'normal');
      }
      doc.save(`${weekFileLabel(week)}.pdf`);
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo exportar esa hoja.' });
    } finally {
      setExportingId(null);
    }
  };

  const handleReopen = async (week) => {
    setLockActionId(week.id);
    setStatus(null);
    try {
      const r = await api.post(`sleever-weeks/${week.id}/reopen/`);
      setWeeks((ws) => ws.map((w) => (w.id === week.id ? r.data : w)));
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo reabrir la semana.' });
    } finally {
      setLockActionId(null);
    }
  };

  const handleLockNow = async (week) => {
    setLockActionId(week.id);
    setStatus(null);
    try {
      const r = await api.post(`sleever-weeks/${week.id}/lock/`);
      setWeeks((ws) => ws.map((w) => (w.id === week.id ? r.data : w)));
    } catch (err) {
      setStatus({ type: 'error', text: 'No se pudo cerrar la semana.' });
    } finally {
      setLockActionId(null);
    }
  };

  const handleDelete = async (week) => {
    if (!window.confirm(`¿Eliminar la hoja de Semana ${week.week_number} / ${week.year} (${week.farm_name})? Se eliminarán sus ${week.entry_count} línea(s).`)) return;
    setDeletingId(week.id);
    setStatus(null);
    try {
      await api.delete(`sleever-weeks/${week.id}/`);
      setWeeks((ws) => ws.filter((w) => w.id !== week.id));
    } catch (err) {
      setStatus({ type: 'error', text: apiErrorText(err, 'No se pudo eliminar esa hoja.') });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="scl-root">
      <div className="page-topbar">
        <Link to="/production" className="topbar-btn">← Producción</Link>
        <Link to="/enfunde" className="topbar-btn topbar-btn-primary">+ Nueva / Editar Semana</Link>
      </div>
      <h1 className="scl-title">📋 Historial de Control de Enfunde</h1>
      <p className="scl-subtitle page-header-divided">Una hoja por semana y finca. Ábrela para seguir editando, o elimínala desde aquí.</p>

      <div className="scl-export-row">
        <button className="scl-export-btn" onClick={() => setPreview({ kind: 'all' })} disabled={filtered.length === 0}>📤 Exportar todo lo filtrado</button>
      </div>

      {status && <div className="scl-status">{status.text}</div>}

      <div className="scl-filters">
        <select className="scl-select" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} aria-label="Año">
          <option value="">Todos los Años</option>
          {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select className="scl-select" value={weekFilter} onChange={(e) => setWeekFilter(e.target.value)} aria-label="Semana">
          <option value="">Todas las Semanas</option>
          {weekOptions.map((n) => <option key={n} value={n}>Semana {n}</option>)}
        </select>
        <select className="scl-select" value={farmFilter} onChange={(e) => setFarmFilter(e.target.value)} aria-label="Finca">
          <option value="">Todas las Fincas</option>
          {farmOptions.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select className="scl-select" value={ribbonFilter} onChange={(e) => setRibbonFilter(e.target.value)} aria-label="Cinta">
          <option value="">Todas las Cintas</option>
          {ribbonOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          {hasSheetsWithoutRibbon && <option value={NO_RIBBON}>— sin cinta —</option>}
        </select>
      </div>

      <div className="scl-summary">
        <div className="scl-summary-card">
          <div className="scl-summary-label">Hojas</div>
          <div className="scl-summary-value">{summary.sheets}</div>
        </div>
        <div className="scl-summary-card">
          <div className="scl-summary-label">Total Trabajadores</div>
          <div className="scl-summary-value">{summary.workers}</div>
        </div>
        <div className="scl-summary-card">
          <div className="scl-summary-label">Total</div>
          <div className="scl-summary-value">{summary.bags}</div>
        </div>
        <div className="scl-summary-card">
          <div className="scl-summary-label">Total/Ha</div>
          <div className="scl-summary-value">{formatPerHa(summary.perHa)}</div>
        </div>
      </div>

      <div className="scl-card">
        {loading ? (
          <div className="scl-empty">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="scl-empty">
            {anyFilter ? 'Sin hojas para los filtros seleccionados.' : 'Aún no hay hojas guardadas.'}
          </div>
        ) : (
          <table className="scl-table">
            <thead>
              <tr>
                <th>Finca</th>
                <th>Año</th>
                <th>Semana</th>
                <th>Cinta</th>
                <th>Primera Vuelta</th>
                <th>Segunda Vuelta</th>
                <th>Total</th>
                <th>Total/Ha</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((w) => (
                <tr
                  key={w.id}
                  className="scl-row"
                  style={w.ribbon_hex_color ? {
                    backgroundColor: hexToRgba(w.ribbon_hex_color, 0.12),
                    borderLeft: `4px solid ${w.ribbon_hex_color}`,
                  } : undefined}
                >
                  <td>{w.farm_name}</td>
                  <td>{w.year}</td>
                  <td>{w.week_number}</td>
                  <td>
                    <span className="scl-ribbon-cell">
                      {w.ribbon_hex_color && (
                        <span className="scl-swatch" style={{ backgroundColor: w.ribbon_hex_color }} />
                      )}
                      {w.ribbon_color || '—'}
                    </span>
                  </td>
                  <td>{w.primera_vuelta}</td>
                  <td>{w.segunda_vuelta}</td>
                  <td>{w.total_bags}</td>
                  <td><HoverNote note={perHaBreakdown(w)}>{formatPerHa(w.total_per_hectare)}</HoverNote></td>
                  <td>
                    {w.is_locked ? (
                      <span className="scl-lock-badge scl-lock-badge-locked">🔒 Cerrada</span>
                    ) : w.reopened_until && new Date(w.reopened_until) > new Date() ? (
                      <span className="scl-lock-badge scl-lock-badge-reopened" title={new Date(w.reopened_until).toLocaleString()}>
                        🔓 Reabierta
                      </span>
                    ) : (
                      <span className="scl-lock-badge scl-lock-badge-open">Abierta</span>
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      {!w.is_locked && (
                        <Link
                          className="row-icon-btn"
                          to={`/enfunde?farm=${w.farm}&year=${w.year}&week=${w.week_number}`}
                          title="Editar"
                          aria-label="Editar"
                        >
                          ✏️
                        </Link>
                      )}
                      <button
                        className="row-icon-btn"
                        onClick={() => openWeekPreview(w)}
                        disabled={exportingId === w.id}
                        title="Exportar esta semana (Excel o PDF)"
                        aria-label="Exportar"
                      >
                        {exportingId === w.id ? '…' : '📤'}
                      </button>
                      <RowProperties item={w} title={`${w.farm_name} · Semana ${w.week_number} de ${w.year}`} />
                      {canReopenWeeks && w.is_locked && (
                        <button
                          className="row-icon-btn"
                          onClick={() => handleReopen(w)}
                          disabled={lockActionId === w.id}
                          title="Reabrir por 48 horas"
                          aria-label="Reabrir"
                        >
                          {lockActionId === w.id ? '…' : '🔓'}
                        </button>
                      )}
                      {canReopenWeeks && !w.is_locked && w.reopened_until && new Date(w.reopened_until) > new Date() && (
                        <button
                          className="row-icon-btn"
                          onClick={() => handleLockNow(w)}
                          disabled={lockActionId === w.id}
                          title="Cerrar ahora"
                          aria-label="Cerrar"
                        >
                          {lockActionId === w.id ? '…' : '🔒'}
                        </button>
                      )}
                      {canDelete && !w.is_locked && (
                        <button
                          className="row-icon-btn"
                          onClick={() => handleDelete(w)}
                          disabled={deletingId === w.id}
                          title="Eliminar"
                          aria-label="Eliminar"
                        >
                          {deletingId === w.id ? '…' : '🗑️'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {preview && (
        <ExportPreviewModal
          title={preview.kind === 'all'
            ? `Historial de Control de Enfunde · ${filtered.length} hoja(s)`
            : `${preview.week.farm_name} · Semana ${preview.week.week_number} de ${preview.week.year}`}
          fileName={preview.kind === 'all' ? 'Historial_Control_de_Enfunde' : weekFileLabel(preview.week)}
          loading={preview.loading}
          error={preview.error}
          onClose={() => setPreview(null)}
          onDownload={handlePreviewDownload}
        >
          {preview.kind === 'all'
            ? <AllWeeksExportPreview weeks={filtered} />
            : preview.detail && <WeekExportPreview week={preview.week} detail={preview.detail} />}
        </ExportPreviewModal>
      )}
    </div>
  );
}
